import styled from 'styled-components';

import { TextField, Autocomplete, Select, InputBase } from '@mui/material';

import { DesktopDatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers';

import { colors } from 'theme';
export const SearchInput = styled(InputBase)`
  color: ${colors.legacy.grey[500]};
  font-size: 1.3rem;
  flex-grow: 1;
`;

export const commonInputStyles = `
  background-color: ${colors.common.white};
  border-radius: 4px;

  .MuiInputBase-root {
    font-size: 16px;
    color: ${colors.text.primary};

    fieldset {
      border: 1px solid ${colors.action.disabledBackground};
    }

    &:hover fieldset {
      border: 1px solid ${colors.grey[700]};
    }

    &.Mui-focused fieldset {
      border: 2px solid ${colors.primary.main};
    }
  }

  .MuiInputBase-input {
    font-size: 16px;

    &::placeholder {
      opacity: 1;
      color: ${colors.text.secondary};
      font-size: 16px;
    }
  }

  // MUI X v8 Date/Time picker input styles
  .MuiOutlinedInput-input {
    font-size: 16px;

    &::placeholder {
      opacity: 1;
      color: ${colors.text.secondary};
      font-size: 16px;
    }
  }

  .Mui-disabled {
    &.MuiInputBase-root {
      fieldset {
        border: 1px solid rgba(0, 0, 0, 0.06);
      }
    }
  }

  .Mui-error {
    margin: 0;

    &.MuiInputBase-root {
      fieldset {
        border: 2px solid ${colors.error.light};
      }
    }
    &.MuiFormHelperText-root {
      margin: 0;
      color: ${colors.error.main};
    }
  }

  .MuiFormHelperText-root {
    color: ${colors.text.secondary};
    font-size: 12px;
    font-weight: 400;
  }

  // to remove background input color in chrome and safari
  input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px ${colors.common.white} inset;
  }
`;

export const StyledTextField = styled(TextField)`
  ${commonInputStyles}

  ${(props) =>
    props.size === 'small' &&
    `
    .MuiInputBase-input,
    .MuiOutlinedInput-input {
      font-size: 14px;
    }
  `}
` as typeof TextField;

export const StyledDatePicker = styled(DesktopDatePicker)`
  ${commonInputStyles}
` as typeof DesktopDatePicker;

export const StyledDateTimePicker = styled(DateTimePicker)`
  ${commonInputStyles}
` as typeof DateTimePicker;

export const StyledTimePicker = styled(TimePicker)`
  ${commonInputStyles}
` as typeof TimePicker;

export const StyledAutocomplete = styled(Autocomplete)`
  ${commonInputStyles}

  .MuiOutlinedInput-root .MuiAutocomplete-input {
    min-width: 0;
  }

  .MuiAutocomplete-tag {
    background-color: #00000014;
    color: #000000de;
    border-radius: 16px;
    padding: 4px 6px;
  }

  // to remove second remove icon in chrome and safari
  input[type='search']::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }
` as typeof Autocomplete;

export const StyledSelect = styled(Select)<{ $withoutBorder?: boolean; $withoutPadding?: boolean }>`
  ${commonInputStyles}
  height: ${(props) => (props.size === 'small' ? '40px' : '56px')};
  .MuiSelect-select {
    max-height: ${(props) => (props.size === 'small' ? '40px' : '56px')};
    display: flex;
    align-items: center;
    ${(props) => props.size === 'small' && 'font-size: 14px;'}
    padding: ${(props) => (props.$withoutPadding ? '0' : '')};
  }
  .Mui-error {
    border: ${(props) => (props.$withoutBorder ? 'none' : ' 2px solid rgba(239, 83, 80, 1)')};
  }
  .MuiOutlinedInput-notchedOutline {
    border: ${(props) => props.$withoutBorder && 'none'};
  }
`;

export const StyledChipSelect = styled(StyledSelect)`
  .MuiSelect-select {
    padding: 9px;
  }
`;

export const SecondaryStyledTextField = styled(TextField)`
  background-color: ${({ theme }) => theme.palette.common.white};
  margin-top: 0;

  label {
    color: ${colors.ui.border.main};
    font-weight: bold;
    transform: initial;
    transform-origin: initial;
    font-size: 1rem;
    line-height: 2rem;
    opacity: ${(props) => (props.disabled ? '0.38' : '1')};

    &.input-label-error {
      color: ${colors.ui.border.main};
    }
  }
  &:focus-within label {
    color: ${colors.primary.main};
  }

  input {
    padding: 8px 0;
    font-size: 1.4rem;
    line-height: 2rem;
    color: $${({ theme }) => theme.palette.primary.dark};
    height: 2rem;
  }

  textarea {
    padding: 8px 0;
    font-size: 1.4rem;
    line-height: 2.4rem;
    color: ${({ theme }) => theme.palette.primary.dark};
  }

  .select-select {
    padding: 8px 0;
    line-height: 2rem;
    font-size: 1.4rem;
    color: ${({ theme }) => theme.palette.primary.dark};
  }

  .select-select.multiple {
    min-width: 180px;
    padding-right: 12px;
  }

  .input-disabled {
    opacity: 0.38;
  }

  .input-root {
    &:before {
      border-bottom: 1px solid ${colors.ui.border.main};
    }

    &.input-underline:after {
      border-bottom-width: 3px;
      border-bottom-color: ${colors.primary.main};
    }

    &.input-underline.input-error:after {
      border-bottom-color: ${({ theme }) => theme.palette.error.main};
    }

    input::placeholder {
      opacity: 0.8;
      color: ${({ theme }) => theme.palette.primary.dark};
    }
  }

  .input-multiline {
    padding: 0;
  }
` as typeof TextField;

export const TextFieldWrapper = styled.div`
  margin-bottom: 16px;
  position: relative;
`;
